import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { 
  Briefcase, Plus, Edit, Trash2, Save, Loader2, FileDown, 
  Globe, XCircle, CheckCircle, MapPin, Clock, DollarSign 
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import logger from '../../utils/logger';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_JOB_FORM = {
  title_fr: '',
  description_fr: '',
  responsibilities_fr: [],
  required_skills_fr: [],
  nice_to_have_fr: [],
  title_en: '',
  description_en: '',
  responsibilities_en: [],
  required_skills_en: [],
  nice_to_have_en: [],
  department: 'Développement',
  location: 'Télétravail / Remote',
  contract_type: 'Temps partiel',
  salary_range: '',
  hours_per_week: '',
  start_date: '',
  contact_email: '',
  is_active: true
};

export default function AdminJobPostings({ adminKey }) {
  const { t, language } = useLanguage();
  const [jobPostings, setJobPostings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [jobDialog, setJobDialog] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [jobFormLang, setJobFormLang] = useState('fr');
  const [jobForm, setJobForm] = useState(DEFAULT_JOB_FORM);
  
  // Input states for list fields
  const [newResponsibility, setNewResponsibility] = useState('');
  const [newSkill, setNewSkill] = useState('');
  const [newNiceToHave, setNewNiceToHave] = useState('');

  const api = useMemo(() => axios.create({
    baseURL: `${API_URL}/api`,
    headers: { 'X-Admin-Key': adminKey }
  }), [adminKey]);

  const fetchJobPostings = useCallback(async () => {
    try {
      const response = await api.get('/admin/job-postings');
      setJobPostings(response.data?.postings || response.data || []);
    } catch (error) {
      logger.error('Error fetching job postings:', error);
      setJobPostings([]);
    }
  }, [api]);

  useEffect(() => {
    fetchJobPostings();
  }, [fetchJobPostings]);

  const resetJobForm = () => {
    setJobForm(DEFAULT_JOB_FORM);
    setJobFormLang('fr');
    setNewResponsibility('');
    setNewSkill('');
    setNewNiceToHave('');
  };

  const openNewJobDialog = () => {
    resetJobForm();
    setEditingJob(null);
    setJobDialog(true);
  };

  const openEditJobDialog = (job) => {
    setJobForm({
      title_fr: job.title_fr || job.title || '',
      description_fr: job.description_fr || job.description || '',
      responsibilities_fr: job.responsibilities_fr || job.responsibilities || [],
      required_skills_fr: job.required_skills_fr || job.required_skills || [],
      nice_to_have_fr: job.nice_to_have_fr || job.nice_to_have || [],
      title_en: job.title_en || '',
      description_en: job.description_en || '',
      responsibilities_en: job.responsibilities_en || [],
      required_skills_en: job.required_skills_en || [],
      nice_to_have_en: job.nice_to_have_en || [],
      department: job.department || 'Développement',
      location: job.location || 'Télétravail / Remote',
      contract_type: job.contract_type || 'Temps partiel',
      salary_range: job.salary_range || '',
      hours_per_week: job.hours_per_week || '',
      start_date: job.start_date || '',
      contact_email: job.contact_email || '',
      is_active: job.is_active !== false
    });
    setJobFormLang('fr');
    setEditingJob(job);
    setJobDialog(true);
  };

  const saveJobPosting = async () => {
    if (!jobForm.title_fr || !jobForm.description_fr) {
      toast.error(language === 'fr' ? 'Le titre et la description en français sont requis' : 'French title and description are required');
      return;
    }
    setLoading(true);
    try {
      if (editingJob) {
        await api.put(`/admin/job-postings/${editingJob.posting_id}`, jobForm);
        toast.success(language === 'fr' ? 'Offre mise à jour!' : 'Posting updated!');
      } else {
        await api.post('/admin/job-postings', jobForm);
        toast.success(language === 'fr' ? 'Offre créée!' : 'Posting created!');
      }
      setJobDialog(false);
      fetchJobPostings();
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur lors de la sauvegarde' : 'Error saving');
    } finally {
      setLoading(false);
    }
  };

  const deleteJobPosting = async (postingId) => {
    if (!window.confirm(language === 'fr' ? 'Supprimer cette position?' : 'Delete this position?')) return;
    try {
      await api.delete(`/admin/job-postings/${postingId}`);
      toast.success(language === 'fr' ? 'Offre supprimée!' : 'Posting deleted!');
      fetchJobPostings();
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur lors de la suppression' : 'Error deleting');
    }
  };

  const downloadJobPdf = async (postingId, title, pdfLang = 'fr') => {
    try {
      const response = await api.get(`/admin/job-postings/${postingId}/pdf?lang=${pdfLang}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `PayHub_Offre_${title.replace(/\s+/g, '_').substring(0, 30)}_${pdfLang.toUpperCase()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(language === 'fr' ? 'PDF téléchargé!' : 'PDF downloaded!');
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur lors du téléchargement' : 'Download error');
    }
  };

  const addToList = (listName, value, setValue) => {
    if (!value.trim()) return;
    setJobForm(prev => ({
      ...prev,
      [listName]: [...(prev[listName] || []), value.trim()]
    }));
    setValue('');
  };

  const removeFromList = (listName, index) => {
    setJobForm(prev => ({
      ...prev,
      [listName]: (prev[listName] || []).filter((_, i) => i !== index)
    }));
  };

  // Render list input component
  const ListInput = ({ listName, value, setValue, placeholder, colorClass }) => (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input 
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={placeholder}
          onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addToList(listName, value, setValue))}
        />
        <Button type="button" variant="outline" onClick={() => addToList(listName, value, setValue)}>
          <Plus className="h-4 w-4" />
        </Button>
      </div>
      <div className="flex flex-wrap gap-2 mt-2">
        {(jobForm[listName] || []).map((item, idx) => (
          <span key={`${item}-${idx}`} className={`inline-flex items-center gap-1 px-3 py-1 ${colorClass} rounded-full text-sm`}>
            {item}
            <button onClick={() => removeFromList(listName, idx)} className="hover:opacity-70">
              <XCircle className="h-4 w-4" />
            </button>
          </span>
        ))}
      </div>
    </div>
  );

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-teal-600" />
                {language === 'fr' ? 'Positions Ouvertes' : 'Open Positions'}
              </CardTitle>
              <CardDescription>
                {language === 'fr' ? 'Gérez les offres d\'emploi de PayHub Québec+' : 'Manage PayHub Québec+ job postings'}
              </CardDescription>
            </div>
            <Button onClick={openNewJobDialog} className="bg-gradient-to-r from-teal-500 to-cyan-600">
              <Plus className="h-4 w-4 mr-2" />
              {language === 'fr' ? 'Nouvelle Position' : 'New Position'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {jobPostings.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Briefcase className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{language === 'fr' ? 'Aucune position créée' : 'No positions created'}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {jobPostings.map((job) => (
                <div key={job.posting_id} className={`p-4 rounded-xl border ${job.is_active ? 'border-border' : 'border-dashed border-muted-foreground/30 opacity-60'}`}>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-semibold">{job.title_fr || job.title}</h4>
                        {job.is_active ? (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                            {language === 'fr' ? 'Active' : 'Active'}
                          </span>
                        ) : (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
                            {language === 'fr' ? 'Inactive' : 'Inactive'}
                          </span>
                        )}
                        {job.title_en && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">EN</span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Briefcase className="h-3.5 w-3.5" />
                          {job.department}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3.5 w-3.5" />
                          {job.location}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3.5 w-3.5" />
                          {job.contract_type}
                        </span>
                        {job.salary_range && (
                          <span className="flex items-center gap-1">
                            <DollarSign className="h-3.5 w-3.5" />
                            {job.salary_range}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => downloadJobPdf(job.posting_id, job.title_fr || job.title, 'fr')}>
                        <FileDown className="h-4 w-4 mr-1" />
                        FR
                      </Button>
                      {job.title_en && (
                        <Button variant="outline" size="sm" onClick={() => downloadJobPdf(job.posting_id, job.title_en, 'en')}>
                          <FileDown className="h-4 w-4 mr-1" />
                          EN
                        </Button>
                      )}
                      <Button variant="outline" size="sm" onClick={() => openEditJobDialog(job)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="sm" className="text-red-500" onClick={() => deleteJobPosting(job.posting_id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Job Posting Dialog */}
      <Dialog open={jobDialog} onOpenChange={setJobDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-teal-600" />
              {editingJob ? t('admin.edit_job_posting') : t('admin.new_job_posting')}
            </DialogTitle>
            <DialogDescription>
              {language === 'fr' 
                ? 'Remplissez les informations en français (requis) et en anglais (optionnel).'
                : 'Fill in the information in French (required) and English (optional).'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Common Fields */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>{t('admin.job_department')}</Label>
                <select
                  className="w-full h-10 px-3 rounded-md border border-input bg-background"
                  value={jobForm.department}
                  onChange={(e) => setJobForm({...jobForm, department: e.target.value})}
                >
                  <option value="Développement">{t('admin.dept_development')}</option>
                  <option value="Design">{t('admin.dept_design')}</option>
                  <option value="Marketing">{t('admin.dept_marketing')}</option>
                  <option value="Support">{t('admin.dept_support')}</option>
                  <option value="Administration">{t('admin.dept_admin')}</option>
                  <option value="Autre">{t('admin.dept_other')}</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label>{t('admin.job_location')}</Label>
                <Input 
                  value={jobForm.location}
                  onChange={(e) => setJobForm({...jobForm, location: e.target.value})}
                  placeholder="Télétravail / Remote"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('admin.job_contract')}</Label>
                <select
                  className="w-full h-10 px-3 rounded-md border border-input bg-background"
                  value={jobForm.contract_type}
                  onChange={(e) => setJobForm({...jobForm, contract_type: e.target.value})}
                >
                  <option value="Temps plein">{t('admin.contract_full_time')}</option>
                  <option value="Temps partiel">{t('admin.contract_part_time')}</option>
                  <option value="Contrat">{t('admin.contract_contract')}</option>
                  <option value="Freelance">{t('admin.contract_freelance')}</option>
                  <option value="Stage">{t('admin.contract_internship')}</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label>{t('admin.job_salary')}</Label>
                <Input 
                  value={jobForm.salary_range}
                  onChange={(e) => setJobForm({...jobForm, salary_range: e.target.value})}
                  placeholder="Ex: 25-35$/h"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('admin.job_hours')}</Label>
                <Input 
                  value={jobForm.hours_per_week}
                  onChange={(e) => setJobForm({...jobForm, hours_per_week: e.target.value})}
                  placeholder="10-20"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('admin.job_start_date')}</Label>
                <Input 
                  value={jobForm.start_date}
                  onChange={(e) => setJobForm({...jobForm, start_date: e.target.value})}
                  placeholder="ASAP"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>{t('admin.job_contact_email')}</Label>
                <Input 
                  value={jobForm.contact_email}
                  onChange={(e) => setJobForm({...jobForm, contact_email: e.target.value})}
                  placeholder="recrutement@payhub.quebec.plus"
                />
              </div>
            </div>

            {/* Language Tabs */}
            <div className="border rounded-lg overflow-hidden">
              <div className="flex border-b">
                <button
                  type="button"
                  className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                    jobFormLang === 'fr' 
                      ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-600' 
                      : 'text-muted-foreground hover:bg-muted/50'
                  }`}
                  onClick={() => setJobFormLang('fr')}
                >
                  <Globe className="h-4 w-4" />
                  Français *
                </button>
                <button
                  type="button"
                  className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                    jobFormLang === 'en' 
                      ? 'bg-purple-50 text-purple-700 border-b-2 border-purple-600' 
                      : 'text-muted-foreground hover:bg-muted/50'
                  }`}
                  onClick={() => setJobFormLang('en')}
                >
                  <Globe className="h-4 w-4" />
                  English
                </button>
              </div>
              
              <div className="p-4 space-y-4">
                {jobFormLang === 'fr' && (
                  <>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <span className="px-2 py-0.5 text-xs rounded bg-blue-100 text-blue-700">FR</span>
                        {t('admin.job_title')} *
                      </Label>
                      <Input 
                        value={jobForm.title_fr}
                        onChange={(e) => setJobForm({...jobForm, title_fr: e.target.value})}
                        placeholder="Ex: Développeur Full-Stack"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <span className="px-2 py-0.5 text-xs rounded bg-blue-100 text-blue-700">FR</span>
                        {t('admin.job_description')} *
                      </Label>
                      <textarea
                        className="w-full min-h-[120px] px-3 py-2 rounded-md border border-input bg-background resize-y"
                        value={jobForm.description_fr}
                        onChange={(e) => setJobForm({...jobForm, description_fr: e.target.value})}
                        placeholder="Décrivez le poste et les attentes..."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <span className="px-2 py-0.5 text-xs rounded bg-blue-100 text-blue-700">FR</span>
                        {t('admin.job_responsibilities')}
                      </Label>
                      <ListInput 
                        listName="responsibilities_fr" 
                        value={newResponsibility} 
                        setValue={setNewResponsibility}
                        placeholder="Ajouter une responsabilité"
                        colorClass="bg-teal-100 text-teal-800"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <span className="px-2 py-0.5 text-xs rounded bg-blue-100 text-blue-700">FR</span>
                        {t('admin.job_required_skills')}
                      </Label>
                      <ListInput 
                        listName="required_skills_fr" 
                        value={newSkill} 
                        setValue={setNewSkill}
                        placeholder="Ajouter une compétence"
                        colorClass="bg-blue-100 text-blue-800"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <span className="px-2 py-0.5 text-xs rounded bg-blue-100 text-blue-700">FR</span>
                        {t('admin.job_nice_to_have')}
                      </Label>
                      <ListInput 
                        listName="nice_to_have_fr" 
                        value={newNiceToHave} 
                        setValue={setNewNiceToHave}
                        placeholder="Ajouter un atout"
                        colorClass="bg-amber-100 text-amber-800"
                      />
                    </div>
                  </>
                )}

                {jobFormLang === 'en' && (
                  <>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <span className="px-2 py-0.5 text-xs rounded bg-purple-100 text-purple-700">EN</span>
                        Job Title
                      </Label>
                      <Input 
                        value={jobForm.title_en}
                        onChange={(e) => setJobForm({...jobForm, title_en: e.target.value})}
                        placeholder="Ex: Full-Stack Developer"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <span className="px-2 py-0.5 text-xs rounded bg-purple-100 text-purple-700">EN</span>
                        Job Description
                      </Label>
                      <textarea
                        className="w-full min-h-[120px] px-3 py-2 rounded-md border border-input bg-background resize-y"
                        value={jobForm.description_en}
                        onChange={(e) => setJobForm({...jobForm, description_en: e.target.value})}
                        placeholder="Describe the position and expectations..."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <span className="px-2 py-0.5 text-xs rounded bg-purple-100 text-purple-700">EN</span>
                        Responsibilities
                      </Label>
                      <ListInput 
                        listName="responsibilities_en" 
                        value={newResponsibility} 
                        setValue={setNewResponsibility}
                        placeholder="Add a responsibility"
                        colorClass="bg-teal-100 text-teal-800"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <span className="px-2 py-0.5 text-xs rounded bg-purple-100 text-purple-700">EN</span>
                        Required Skills
                      </Label>
                      <ListInput 
                        listName="required_skills_en" 
                        value={newSkill} 
                        setValue={setNewSkill}
                        placeholder="Add a skill"
                        colorClass="bg-blue-100 text-blue-800"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <span className="px-2 py-0.5 text-xs rounded bg-purple-100 text-purple-700">EN</span>
                        Nice to Have
                      </Label>
                      <ListInput 
                        listName="nice_to_have_en" 
                        value={newNiceToHave} 
                        setValue={setNewNiceToHave}
                        placeholder="Add a nice-to-have"
                        colorClass="bg-amber-100 text-amber-800"
                      />
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Active Status */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={jobForm.is_active}
                onChange={(e) => setJobForm({...jobForm, is_active: e.target.checked})}
                className="rounded"
              />
              <Label htmlFor="is_active">{t('admin.job_is_active')}</Label>
            </div>

            {/* Summary */}
            <div className="p-3 bg-muted/30 rounded-lg text-sm">
              <span className="font-medium">{language === 'fr' ? 'Résumé:' : 'Summary:'} </span>
              {jobForm.title_fr ? (
                <span className="text-green-600">✓ FR ({jobForm.title_fr.substring(0, 30)}{jobForm.title_fr.length > 30 ? '...' : ''})</span>
              ) : (
                <span className="text-red-500">✗ FR (requis/required)</span>
              )}
              {' | '}
              {jobForm.title_en ? (
                <span className="text-green-600">✓ EN ({jobForm.title_en.substring(0, 30)}{jobForm.title_en.length > 30 ? '...' : ''})</span>
              ) : (
                <span className="text-muted-foreground">○ EN (optionnel/optional)</span>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setJobDialog(false)}>{t('admin.cancel')}</Button>
            <Button 
              onClick={saveJobPosting} 
              disabled={loading || !jobForm.title_fr || !jobForm.description_fr}
              className="bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              <Save className="h-4 w-4 mr-2" />
              {editingJob ? t('admin.update_posting') : t('admin.create_posting')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
